"use client"

import { useTranslations } from "next-intl"
import {
  Paper,
  Text,
  Group,
  Stack,
  Button,
} from "@forestsync/ui"
import type { MarketData } from "@/actions/sink-detail"

const MARKETPLACE_URL = process.env.NEXT_PUBLIC_MARKETPLACE_URL ?? "https://carbonomex.com"

interface MarketDataTabProps {
  data: MarketData | null
  creditType?: string | null
  vintage?: number | null
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Paper withBorder p="md" radius="md" className="flex-1 min-w-[130px]">
      <Stack gap={2}>
        <Text size="xs" c="dimmed" fw={500}>
          {label}
        </Text>
        <Text size="lg" fw={700}>
          {value}
        </Text>
        {sub && (
          <Text size="xs" c={sub.startsWith("+") ? "green" : sub.startsWith("-") ? "red" : "dimmed"}>
            {sub}
          </Text>
        )}
      </Stack>
    </Paper>
  )
}

export function MarketDataTab({ data, creditType, vintage }: MarketDataTabProps) {
  const t = useTranslations("glassroom.marketData")
  const marketUrl = MARKETPLACE_URL + (creditType && vintage
    ? `/market?creditType=${encodeURIComponent(creditType)}&vintage=${vintage}`
    : "/market")

  if (!data) {
    return (
      <Stack gap="md" align="center" py="xl">
        <Text c="dimmed">{t("noMarketData")}</Text>
        <Button component="a" href={marketUrl} variant="light">
          {t("tradeOnExchange")}
        </Button>
      </Stack>
    )
  }

  const fmt = (n: number | null) =>
    n == null ? "N/A" : `€${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const changeSub = data.priceChange24h == null
    ? undefined
    : `${data.priceChange24h >= 0 ? "+" : ""}${data.priceChange24h.toFixed(2)}%`

  return (
    <Stack gap="lg">
      {/* Stat cards row */}
      <Group gap="sm" wrap="wrap">
        <StatCard label={t("lastPrice")} value={fmt(data.lastPrice)} sub={changeSub} />
        <StatCard label={t("volume24h")} value={`${data.volume24h.toLocaleString()} tCO₂e`} />
        <StatCard label={t("bestBid")} value={fmt(data.bestBid)} />
        <StatCard label={t("bestAsk")} value={fmt(data.bestAsk)} />
      </Group>

      {/* CTA */}
      <Group>
        <Button
          component="a"
          href={marketUrl}
          variant="filled"
          size="sm"
        >
          {t("tradeOnExchange")}
        </Button>
      </Group>
    </Stack>
  )
}
